import { createColumnHelper } from "@tanstack/react-table";
import {
  Activity,
  ActivityQuery,
  AuthConfig,
  ControlCode,
  ControlCodes,
  cn,
  createRecordFromArray,
  createState,
  displayPlatform,
  EndpointActivity,
  ExitCode,
  ExitCodeGroup,
  formatDate,
  getStatusBorderColor,
  getStatusColor,
  isAdminUser,
  Platform,
  Probe,
  parseDate,
  queries,
  Test,
  TestsActivity,
  Threat,
  ThreatsActivity,
  TimeFormats,
  useAccountQuery,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  CheckmarkIcon,
  CloseIcon,
  CopyButton,
  DownloadIcon,
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownRadio,
  DropdownRadioGroup,
  DropdownTrigger,
  IconButton,
  MoreIcon,
  OverlayInfoBlock,
  PlatformIcon,
  RotatingChevron,
  SearchIcon,
  ToggleGroup,
  ToggleItem,
  useDownloadFile,
} from "@theprelude/ds";
import { useMemo, useState } from "react";
import { z } from "zod";
import {
  DeleteEndpointModal,
  useDeleteEndpointModal,
} from "@/components/modal/delete-endpoint-modal";
import { usePopper } from "@/hooks/use-popper";
import { createQuery } from "@/lib/create-query";
import {
  ExpectedOutcome,
  ExpectedResult,
  getExpectationStatus,
  getExpectedName,
  getExpectedResult,
  getPersonalPercentages,
  getProtectedPercentage,
  OptionalODPFields,
} from "@/lib/odp";
import { getActivityQuery } from "@/lib/validators";
import { getEndpointData, Host } from "@/queries/endpoints";
import { DeepLink, DeepLinkActionsColumn, DeepPolicyLink } from "../deep-links";
import { DefaultOverlay } from "../default-overlay";
import { EndpointTags } from "../endpoint-tags/endpoint-tags";
import { hasFeatures } from "../flag/flag";
import { NonApplicablePercent } from "../protection-blocks/protection-blocks";
import {
  detectedResultColumn,
  expectedOutcomeColumn,
  lastResultStatusColumn,
  observedResultColumn,
  personalExpectedOutcomeColumn,
  preventedStatusColumn,
  sourceIdColumn,
  techniqueColumn,
  testIdColumn,
  testNameColumn,
  testNameSmallColumn,
  threatIdColumn,
  threatNameColumn,
  threatNameSmallColumn,
  timeColumn,
} from "../table/columns";
import { TableFilterInput } from "../table/filter-input";
import { useTable } from "../table/hooks";
import { VirtualTable } from "../table/virtual-table";

type EndpointTestsActivityEntry = {
  testId: string;
  name: string;
  author: string | undefined;
  expectedResult?: ExpectedResult;
  odpFields: OptionalODPFields;
  technique?: string;
  protected?: number;
  benchmark?: number;
  account_id: string | undefined;
};

type EndpointThreatsActivityEntry = {
  source_id?: string;
  source?: string;
  threatId: string;
  name: string;
  protected?: number;
  benchmark?: number;
};

type EndpointResult = {
  author: string | undefined;
  control: ControlCode;
  date: string;
  detected: string | undefined;
  dos: Platform;
  edrId: string | undefined;
  expected_outcome: ExpectedOutcome | undefined;
  host: string | undefined;
  observed: string | undefined;
  status: ExitCode;
  technique: string | undefined;
  testId: string | undefined;
  testName: string | undefined;
  threatId: string | undefined;
  threatName: string | undefined;
  account_id: string | undefined;
};

const deepLinkedControls: Set<ControlCode> = new Set([
  ControlCodes.CROWDSTRIKE,
  ControlCodes.DEFENDER,
  ControlCodes.DEFENDER_DISCOVERY,
  ControlCodes.SENTINELONE,
]);

const getGreaterDate = (date1: Date | null, date2: Date | null) => {
  if (!date1 && !date2) return null;
  if (!date1) return date2;
  if (!date2) return date1;

  return date1 > date2 ? date1 : date2;
};

export const detectEndpointSchema = z.tuple([
  z.literal("detect-endpoint"),
  z.object({
    id: z.string(),
  }),
]);

type EndpointSchema = z.infer<typeof detectEndpointSchema>;

function getEndpointOverlayData(id: string) {
  return (
    endpoints: Probe[],
    tests: Test[],
    threats: Threat[],
    activity: Activity[],
    endpointsActivity: EndpointActivity[],
    testsActivity: TestsActivity[],
    threatsActivity: ThreatsActivity[],
    endpointLogs: Activity[],
  ) => {
    const testsById = createRecordFromArray(tests, "id");
    const threatsById = createRecordFromArray(threats, "id");
    const endpointsById = createRecordFromArray(endpoints, "endpoint_id");

    const endpointActivity = endpointsActivity.find(
      (activity) => activity.id === id,
    );

    const testActivity: EndpointTestsActivityEntry[] = testsActivity
      .map((activity) => {
        return {
          account_id: testsById[activity.id]?.account_id ?? undefined,
          testId: activity.id,
          name: testsById[activity.id]?.name ?? "Unknown",
          author: testsById[activity.id]?.author ?? undefined,
          odpFields: getPersonalPercentages(activity.personal),
          expectedResult: getExpectedResult(
            activity.expected,
            activity.personal,
          ),
          technique: testsById[activity.id]?.technique ?? undefined,
          protected: activity.personal
            ? getProtectedPercentage(activity.personal)
            : undefined,
          benchmark: activity.social ?? undefined,
        };
      })
      .filter((activity) => activity.name !== "Unknown");

    const threatActivity: EndpointThreatsActivityEntry[] = threatsActivity
      .map((activity) => {
        return {
          source_id: threatsById[activity.id]?.source_id ?? undefined,
          source: threatsById[activity.id]?.source ?? undefined,
          threatId: activity.id,
          name: threatsById[activity.id]?.name ?? "Unknown",
          protected: activity.protected ?? undefined,
          benchmark: activity.social ?? undefined,
        };
      })
      .filter((activity) => activity.name !== "Unknown");

    const host = getEndpointData(endpoints, endpointsActivity).find(
      (host) => host.endpoint_id === id,
    );

    const results: EndpointResult[] = activity.map((activity) => {
      const expectation = getExpectedName(
        testsById[activity.test]?.expected.crowdstrike,
      );
      const isExpected = getExpectationStatus(activity, expectation);
      return {
        ...activity,
        account_id: testsById[activity.test]?.account_id ?? undefined,
        author: testsById[activity.test]?.author ?? undefined,
        detected: activity.detected ?? undefined,
        edrId: endpointsById[activity.endpoint_id]?.edr_id ?? undefined,
        expected_outcome: expectation ? { expectation, isExpected } : undefined,
        host: endpointsById[activity.endpoint_id]?.host ?? undefined,
        observed: activity.observed ?? undefined,
        testId: activity.test,
        testName: testsById[activity.test]?.name,
        threatName: activity.threat
          ? threatsById[activity.threat]?.name
          : undefined,
        threatId: activity.threat ?? undefined,
        technique: activity.technique ?? undefined,
      };
    });

    return {
      host,
      tests,
      endpointActivity,
      testActivity,
      threatActivity,
      endpointLogs,
      results,
    };
  };
}

const overlayQuery = (
  authConfig: AuthConfig,
  query: ActivityQuery,
  id: string,
) =>
  createQuery({
    queries: [
      queries.detect.endpoints(authConfig, 90),
      queries.detect.tests(authConfig),
      queries.detect.threats(authConfig),
      queries.detect.activity.logs(authConfig, { ...query, endpoints: id }),
      queries.detect.activity.endpoints(authConfig, {
        ...query,
        endpoints: id,
      }),
      queries.detect.activity.tests(authConfig, { ...query, endpoints: id }),
      queries.detect.activity.threats(authConfig, {
        ...query,
        endpoints: id,
      }),
      queries.detect.activity.logs(authConfig, { ...query, endpoints: id }),
    ],
    queryFn: getEndpointOverlayData(id),
  });

const useEndpointView = createState<"threats" | "tests" | "results">("threats");

function DetectEndpointOverlay({ id }: EndpointSchema[1]) {
  return (
    <DefaultOverlay
      title={"Endpoint"}
      actionsDropdown={<EndpointDropdown id={id} />}
    >
      <EndpointView id={id} />
    </DefaultOverlay>
  );
}

const EndpointView = ({ id }: EndpointSchema[1]) => {
  const view = useEndpointView((state) => state.value);
  const authConfig = useAuthConfig();
  const query = getActivityQuery();
  const account = useAccountQuery();

  const endpointOverlayQuery = overlayQuery(
    authConfig,
    query,
    id,
  ).useSuspenseQuery();
  const endpointOverlay = endpointOverlayQuery.data;
  const host = endpointOverlay?.host;
  const results = endpointOverlay.results;

  if (!host) return null;

  const controlIsActive =
    host.control !== ControlCodes.NONE &&
    Boolean(
      account.data?.controls.find((control) => control.id === host.control),
    );

  const isDeepLinked =
    host.control !== null &&
    host.control !== undefined &&
    host.control !== ControlCodes.NONE &&
    deepLinkedControls.has(host.control);

  const textColor = getStatusColor(host.performance);
  const borderColor = getStatusBorderColor(host.performance);

  const lastSeenDate = host.last_seen ? parseDate(host.last_seen) : null;

  const endpointLogs = endpointOverlay?.endpointLogs ?? [];

  const lastActivityDate = endpointLogs[0]?.date
    ? parseDate(endpointLogs[0]?.date)
    : null;

  const lastSeen = getGreaterDate(lastSeenDate, lastActivityDate);
  const toggleViewCounts = {
    tests: endpointOverlay?.testActivity.length ?? 0,
    threats: endpointOverlay?.threatActivity.length ?? 0,
    logs: endpointOverlay?.endpointLogs.length ?? 0,
  };

  return (
    <>
      <div className="flex flex-col">
        <section className="flex flex-col gap-4 p-4">
          <OverlayInfoBlock title={"Hostname"}>
            <div className="flex flex-col gap-1">
              <h3 className="m-0 text-2xl leading-8">{host.host}</h3>

              {host.dos && (
                <div className="flex items-center gap-1">
                  <PlatformIcon
                    className="size-4 flex-shrink-0 text-secondary-foreground"
                    platform={host.dos}
                  />
                  <span>{displayPlatform(host.dos)}</span>
                </div>
              )}
            </div>
          </OverlayInfoBlock>

          {host.serial_num && (
            <OverlayInfoBlock title={"Serial Number"}>
              <span>{host.serial_num}</span>
            </OverlayInfoBlock>
          )}

          {lastSeen && (
            <OverlayInfoBlock
              title={`Probe checked in on ${formatDate(
                lastSeen,
                TimeFormats.DATE_SHORT,
              )}`}
            >
              <div className="flex items-center gap-1 text-primary-foreground">
                {host.isActive ? (
                  <>
                    <CheckmarkIcon />
                    <p>Probe Active</p>
                  </>
                ) : (
                  <>
                    <CloseIcon />
                    <p>Probe Inactive</p>
                  </>
                )}
              </div>
            </OverlayInfoBlock>
          )}

          {host.performance === undefined ? (
            <NonApplicablePercent />
          ) : (
            <div
              className={cn(
                "flex w-max flex-col border border-stroke-line p-4",
                textColor,
                borderColor,
              )}
            >
              <span className="font-medium text-sm">Protected</span>
              <span className="text-4xl">{host.performance}%</span>
            </div>
          )}

          <OverlayInfoBlock title={"Tags"}>
            <div className="pt-1">
              <EndpointTags
                tags={host.tags ?? []}
                endpoint_id={host.endpoint_id}
              />
            </div>
          </OverlayInfoBlock>

          {host.edr_id && (
            <OverlayInfoBlock title={"Endpoint Detection & Response (EDR)"}>
              <>
                {controlIsActive && host.control && isDeepLinked ? (
                  <div className="flex items-center gap-1">
                    <DeepLink control={host.control} edrID={host.edr_id} />
                    <CopyButton toCopy={host.edr_id} />
                  </div>
                ) : (
                  <div>
                    <span>{host.edr_id}</span>
                    <CopyButton toCopy={host.edr_id} />
                  </div>
                )}
              </>
            </OverlayInfoBlock>
          )}

          <OverlayInfoBlock title={"Prevention Policy"}>
            <div className="max-w-md flex-column">
              {!host.policy && (
                <span className="text-secondary-foreground">None</span>
              )}
              {host.policy_name && host.policy && (
                <div className="flex items-center gap-1">
                  <span className="break-all text-base">
                    {host.policy_name}
                  </span>
                  <DeepPolicyLink control={host.control} policy={host.policy} />
                  <CopyButton toCopy={host.policy_name} />
                </div>
              )}
              {host.policy && (
                <div className="flex items-center gap-1 text-secondary-foreground">
                  <span className="break-all text-sm">{host.policy}</span>
                  <CopyButton toCopy={host.policy} />
                </div>
              )}
            </div>
          </OverlayInfoBlock>
        </section>
      </div>
      {view === "results" && (
        <EndpointResultsTable
          host={host}
          data={results}
          counts={toggleViewCounts}
        />
      )}
      {view === "threats" && (
        <EndpointThreatsActivity
          data={endpointOverlay?.threatActivity ?? []}
          counts={toggleViewCounts}
        />
      )}
      {view === "tests" && (
        <EndpointTestsActivity
          data={endpointOverlay?.testActivity ?? []}
          counts={toggleViewCounts}
        />
      )}
    </>
  );
};

function EndpointDropdown({ id }: { id: string }) {
  const setDeleteEndpointModal = useDeleteEndpointModal(
    (state) => state.setValue,
  );
  const account = useAccountQuery();
  const isAdmin = isAdminUser(account.data) ?? false;

  return (
    <>
      <DeleteEndpointModal endpointIds={[id]} />
      <Dropdown>
        <DropdownTrigger asChild>
          {<IconButton icon={<MoreIcon />} />}
        </DropdownTrigger>
        <DropdownContent align="end">
          <DropdownItem
            onSelect={() => setDeleteEndpointModal(true)}
            disabled={!isAdmin}
          >
            Delete this endpoint
          </DropdownItem>
        </DropdownContent>
      </Dropdown>
    </>
  );
}

const EndpointResultsTable: React.FC<{
  host: Host;
  data: EndpointResult[];
  counts: { tests: number; threats: number; logs: number };
}> = ({ host, data, counts }) => {
  const [activeSearch, setActiveSearch] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const dropdown = usePopper();
  const logsData = useMemo(() => {
    return (data || []).filter((log) => {
      const isPass = (ExitCodeGroup.PROTECTED as unknown as number[]).includes(
        log.status,
      );
      const isFail = (
        ExitCodeGroup.UNPROTECTED as unknown as number[]
      ).includes(log.status);
      const isError = (ExitCodeGroup.ERROR as unknown as number[]).includes(
        log.status,
      );

      if (statusFilter === "Prevented") return isPass;
      if (statusFilter === "Not prevented") return isFail;
      if (statusFilter === "Error") return isError;

      return true;
    });
  }, [data, statusFilter]);

  const columns = useMemo(() => {
    const columnHelper = createColumnHelper<EndpointResult>();
    return [
      columnHelper.accessor("testName", {
        ...testNameColumn,
        size: 160,
        meta: {
          ...testNameColumn.meta,
        },
      }),
      columnHelper.accessor("threatName", {
        ...threatNameColumn,
        size: 140,
        meta: { ...threatNameColumn.meta },
      }),
      columnHelper.accessor("technique", techniqueColumn),
      columnHelper.accessor("date", timeColumn),
      columnHelper.accessor("observed", observedResultColumn),
      columnHelper.accessor("detected", detectedResultColumn),
      columnHelper.accessor("status", preventedStatusColumn),
      columnHelper.accessor("expected_outcome", expectedOutcomeColumn),
      columnHelper.display({
        id: "actions",
        enableResizing: false,
        enableHiding: false,
        cell: (info) => {
          return <DeepLinkActionsColumn {...info.row.original} />;
        },
        size: 50,
        meta: {
          authorize: (account) =>
            hasFeatures(account, ["detections"]) &&
            account.controls.some((c) => c.id === ControlCodes.CROWDSTRIKE),
        },
      }),
    ];
  }, []);

  const table = useTable({
    name: "endpoint-results",
    data: logsData,
    columns,
    initialState: {
      sorting: [{ id: timeColumn.id, desc: true }],
      columnVisibility: {
        [observedResultColumn.id]: false,
        [detectedResultColumn.id]: false,
        [threatNameColumn.id]: false,
      },
    },
  });

  const downloadReport = useDownloadFile({
    data: JSON.stringify(logsData, null, 2),
    filename: `${host.endpoint_id}-log.json`,
    type: "application/json",
  });

  const statusOptions = ["All", "Prevented", "Error", "Not prevented"];

  return (
    <section className="flex flex-col border-stroke-line border-t">
      <div className="flex h-16 items-center gap-4 p-4">
        {!activeSearch && (
          <ToggleView
            threatActivityCount={counts.threats}
            testActivityCount={counts.tests}
            logActivityCount={counts.logs}
          />
        )}

        {!activeSearch && data.length > 0 && (
          <Button
            intent="pill"
            icon={<DownloadIcon className="text-secondary-foreground" />}
            onClick={() => downloadReport.execute()}
          >
            Export
          </Button>
        )}

        {!activeSearch && (
          <Dropdown {...dropdown}>
            <DropdownTrigger asChild>
              <Button
                intent="pill"
                icon={
                  <RotatingChevron
                    className="text-secondary-foreground"
                    active={dropdown.open}
                  />
                }
                iconPosition="right"
              >
                {statusFilter}
              </Button>
            </DropdownTrigger>

            <DropdownContent className="w-28">
              <DropdownRadioGroup
                value={statusFilter}
                onValueChange={setStatusFilter}
              >
                {statusOptions.map((s) => (
                  <DropdownRadio key={s} value={s}>
                    {s}
                  </DropdownRadio>
                ))}
              </DropdownRadioGroup>
            </DropdownContent>
          </Dropdown>
        )}

        {activeSearch ? (
          <TableFilterInput
            className="lg:w-full"
            table={table}
            onClose={() => setActiveSearch(false)}
          />
        ) : (
          <IconButton
            icon={<SearchIcon />}
            onClick={() => setActiveSearch(true)}
          />
        )}
      </div>
      <div className="border-stroke-line border-b border-dashed" />

      <div className="p-4">
        <VirtualTable
          className="max-h-[500px]"
          table={table}
          emptyState={
            <span className="flex justify-center">
              There are no results for this filter. Please try a different one.
            </span>
          }
        />
      </div>
    </section>
  );
};

const EndpointThreatsActivity: React.FC<{
  data: EndpointThreatsActivityEntry[];
  counts: { tests: number; threats: number; logs: number };
}> = ({ data, counts }) => {
  const [activeSearch, setActiveSearch] = useState<boolean>(false);
  const columnHelper = createColumnHelper<EndpointThreatsActivityEntry>();
  const columns = [
    columnHelper.accessor("threatId", threatIdColumn),
    columnHelper.accessor("source_id", sourceIdColumn),
    columnHelper.accessor("name", threatNameSmallColumn),
    columnHelper.accessor("protected", lastResultStatusColumn),
  ];

  const table = useTable({
    name: "endpoint-threats",
    data,
    columns,
    initialState: {
      columnVisibility: { [threatIdColumn.id]: false },
    },
  });

  return (
    <section className="flex flex-col border-stroke-line border-t">
      <div className="flex h-16 items-center p-4">
        {!activeSearch && (
          <ToggleView
            threatActivityCount={counts.threats}
            testActivityCount={counts.tests}
            logActivityCount={counts.logs}
          />
        )}
        {activeSearch ? (
          <TableFilterInput
            table={table}
            onClose={() => setActiveSearch(false)}
          />
        ) : (
          <IconButton
            icon={<SearchIcon />}
            onClick={() => setActiveSearch(true)}
          />
        )}
      </div>
      <div className="border-stroke-line border-b border-dashed" />

      <div className="p-4">
        <VirtualTable
          className="h-[calc(100vh-760px)] min-h-[500px]"
          table={table}
        />
      </div>
    </section>
  );
};

const EndpointTestsActivity: React.FC<{
  data: EndpointTestsActivityEntry[];
  counts: { tests: number; threats: number; logs: number };
}> = ({ data, counts }) => {
  const [activeSearch, setActiveSearch] = useState<boolean>(false);
  const columnHelper = createColumnHelper<EndpointTestsActivityEntry>();
  const columns = [
    columnHelper.accessor("testId", testIdColumn),
    columnHelper.accessor("name", testNameSmallColumn),
    columnHelper.accessor("technique", techniqueColumn),
    columnHelper.accessor("expectedResult", personalExpectedOutcomeColumn),
  ];

  const table = useTable({
    name: "endpoint-tests",
    data,
    columns,
    initialState: {
      columnVisibility: { [testIdColumn.id]: false },
    },
  });

  return (
    <section className="flex flex-col border-stroke-line border-t">
      <div className="flex h-16 items-center p-4">
        {!activeSearch && (
          <ToggleView
            threatActivityCount={counts.threats}
            testActivityCount={counts.tests}
            logActivityCount={counts.logs}
          />
        )}
        {activeSearch ? (
          <TableFilterInput
            table={table}
            onClose={() => setActiveSearch(false)}
          />
        ) : (
          <IconButton
            icon={<SearchIcon />}
            onClick={() => setActiveSearch(true)}
          />
        )}
      </div>
      <div className="border-stroke-line border-b border-dashed" />

      <div className="p-4">
        <VirtualTable
          className="h-[calc(100vh-760px)] min-h-[500px]"
          table={table}
        />
      </div>
    </section>
  );
};

const ToggleView: React.FC<{
  threatActivityCount: number;
  testActivityCount: number;
  logActivityCount: number;
}> = ({ threatActivityCount, testActivityCount, logActivityCount }) => {
  const view = useEndpointView((state) => state.value);
  const setView = useEndpointView((state) => state.setValue);

  return (
    <div className="mr-auto">
      <ToggleGroup value={view} onValueChange={setView}>
        <ToggleItem value="threats" count={threatActivityCount}>
          Threats
        </ToggleItem>
        <ToggleItem value="tests" count={testActivityCount}>
          Tests
        </ToggleItem>
        <ToggleItem value="results" count={logActivityCount}>
          Results
        </ToggleItem>
      </ToggleGroup>
    </div>
  );
};

export default DetectEndpointOverlay;
